import {
  Create,
  Datagrid,
  Edit,
  List,
  TextField,
  TopToolbar,
  UrlField,
  useDataProvider,
  useNotify,
  useRedirect,
  useResourceContext,
} from 'react-admin';
import { ESTDateField } from './custom-ra-fields/est-date-field';
import { ClicksCreativeForm } from './forms/clicks-creatives';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ClickPreviewPositionMenuItem } from './buttons/click-preview-position-menu';
import { Menu, Button } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecordContext } from 'react-admin';
import { useTransformWithCampaignId } from './forms/hooks/useTransformWithCampaignId';
import {
  resolveCreativeParentLineItemShowPath,
  withPlacementsLineItemIdFromNativeForEdit,
} from 'src/client/utility/pio-native-lineitem-fk-reads';

export const CreativesListActions = () => <TopToolbar />; // return empty tool bar to keep the spacing consistent

export const ClicksCreativesList = () => (
  <List actions={<CreativesListActions />}>
    <Datagrid rowClick="edit">
      <TextField source="id" />
      <TextField source="placementsLineItemId" />
      <TextField source="title" />
      <UrlField source="url" target="_blank" />
      <ESTDateField source="startsAt" />
      <ESTDateField source="endsAt" />
    </Datagrid>
  </List>
);

interface ClicksCreativeCreateInitialValues {
  placementsLineItemId: string;
}

export const ClicksCreativeCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [searchParams] = useSearchParams();
  const placementsLineItemId = searchParams.get('placementsLineItemId') ?? '';
  const transform = useTransformWithCampaignId();

  const resource = useResourceContext();

  const record: ClicksCreativeCreateInitialValues = {
    placementsLineItemId,
  };

  // The legacy /line_items show page can no longer load (PIO API shut off), so
  // resolve a legacy numeric id to its native UUID and land on /lineItems.
  // Prefer the submitted selection over the seeding query param — the user may
  // have picked a different line item in the form.
  const onSuccess = async (data?: Record<string, any>, variables?: Record<string, any>) => {
    notify('Saved new Clicks Creative');
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, variables?.data, { placementsLineItemId });
    redirect(path ?? 'list', resource);
  };

  return (
    <Create record={record} mutationOptions={{ onSuccess }} redirect="list" transform={transform}>
      <ClicksCreativeForm type="Create" />
    </Create>
  );
};

export const ClicksCreativeEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const resource = useResourceContext();

  const onSuccess = async (data?: Record<string, any>, variables?: Record<string, any>) => {
    notify('Saved Clicks Creative');
    // MD-5145: the UpdateClicksCreative response only returns `id`, so resolve the
    // parent line item from the edited record (submitted data / previousData),
    // falling back to the response. All rows route to the native /lineItems show
    // page; a legacy-only id is translated via externalPioId.
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, variables?.data, variables?.previousData, data);
    redirect(path ?? 'list', resource);
  };

  return (
    <Edit
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      redirect="list"
      queryOptions={{ select: withPlacementsLineItemIdFromNativeForEdit }}
    >
      <ClicksCreativeForm type="Update" />
    </Edit>
  );
};

/** A Button that when clicked, displays a menu of links that open a browser tab to preview the Click in a specified position*/
export const PreviewClickButton = () => {
  const record = useRecordContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const positionOptions = ['top', 'bottom', 'right', 'well'];

  return record?.id ? (
    <>
      <div>
        <Button
          onClick={handleClick}
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          endIcon={<KeyboardArrowDownIcon />}
          size="small"
        >
          Preview
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {positionOptions.map(position => {
            return (
              <ClickPreviewPositionMenuItem
                key={position}
                position={position}
                record={record}
                setAnchorEl={setAnchorEl}
              />
            );
          })}
        </Menu>
      </div>
    </>
  ) : null;
};
